import React from 'react';
import { useService } from '@xstate/react';
import { productBuilderMachine } from '../../../context/ProductBuilder/machine';
import { getFormatedPrice } from '../../../helpers/getFormatedPrice';
import { isCurrentState } from '../../../helpers/isCurrentState';
import { ProductBuilderMachineEvent } from '../../../types';
import { Caption } from '../../Common/Caption';
import { Extra } from '../../Common/Extra';
import { Heading } from '../../Common/Heading';
import { Selector, SelectorItem } from '../../Common/Selector';
import { SectionContent } from './SectionContent';

type OnClick = (
  wand_colour: string | number,
  price: number,
  variantId: number,
  send: (event: ProductBuilderMachineEvent) => void,
) => () => void;

const onClick: OnClick = (wand_colour, price, variantId, send) => () => {
  send({
    type: 'UPDATE_CUSTOM',
    custom: {
      wand_colour,
      wand_colour_variant_id: variantId,
      wand_colour_price: price,
    } as any,
  });
};

export const WandColour = () => {
  const [state, send] = useService(productBuilderMachine);
  const { custom: { wand_colour, wand_colour_variant_id, wand_option, wand_colour_price }, wandblack, wandwhite } = state.context;

  // Helper function to find variant price by variant ID
  const findVariantPrice = (variantId: number): number => {
    if (!variantId) return 0;
    
    // Search in white wand variants
    if (wandwhite && Array.isArray(wandwhite) && wandwhite.length > 0) {
      const whiteProduct = wandwhite[0];
      if (whiteProduct?.variants) {
        const variant = whiteProduct.variants.find((v: any) => v.id === variantId);
        if (variant) return variant.price;
      }
    }
    
    // Search in black wand variants
    if (wandblack && Array.isArray(wandblack) && wandblack.length > 0) {
      const blackProduct = wandblack[0];
      if (blackProduct?.variants) {
        const variant = blackProduct.variants.find((v: any) => v.id === variantId);
        if (variant) return variant.price;
      }
    }
    
    return 0;
  };

  // Calculate which wand variant to show (simplified for single variant products)
  const getWandColourOptions = () => {
    const options: Array<{
      id: string;
      title: string;
      price: number;
      variantId: number;
    }> = [];
    
    // Add black wand option (single variant)
    if (wandblack && Array.isArray(wandblack) && wandblack.length > 0) {
      const blackProduct = wandblack[0];
      if (blackProduct && blackProduct.variants && Array.isArray(blackProduct.variants) && blackProduct.variants.length > 0) {
        const selectedVariant = blackProduct.variants[0]; // Use first (and only) variant
        if (selectedVariant) {
          options.push({
            id: 'black',
            title: selectedVariant.name || `Black - ${selectedVariant.title}`,
            price: selectedVariant.price,
            variantId: selectedVariant.id
          });
        }
      }
    }
    
    // Add white wand option (single variant)
    if (wandwhite && Array.isArray(wandwhite) && wandwhite.length > 0) {
      const whiteProduct = wandwhite[0];
      if (whiteProduct && whiteProduct.variants && Array.isArray(whiteProduct.variants) && whiteProduct.variants.length > 0) {
        const selectedVariant = whiteProduct.variants[0]; // Use first (and only) variant
        if (selectedVariant) {
          options.push({
            id: 'white',
            title: selectedVariant.name || `White - ${selectedVariant.title}`,
            price: selectedVariant.price,
            variantId: selectedVariant.id
          });
        }
      }
    }
    
    return options;
  };

  const wandColourOptions = getWandColourOptions();

  // Set default white wand selection when wand option is Yes
  React.useEffect(() => {
    // Only recalculate price when user is on this step (not automatically)
    // This ensures addon prices are only added when user reaches this step
    const onWandColourStep = isCurrentState('wand_colour', state);
    
    if (
      onWandColourStep &&
      wand_option === 'Yes' &&
      wand_colour_variant_id &&
      (!wand_colour_price || wand_colour_price === 0)
    ) {
      const recalculatedPrice = findVariantPrice(wand_colour_variant_id);
      if (recalculatedPrice > 0) {
        send({
          type: 'UPDATE_CUSTOM',
          custom: {
            wand_colour_price: recalculatedPrice,
          },
        });
        return; // Exit early after recalculating price
      }
    }

    if (wand_option === 'Yes' && wandColourOptions.length > 0) {
      // If no wand colour variant is selected, default to white
      if (!wand_colour_variant_id) {
        const whiteOption = wandColourOptions.find(opt => opt.id === 'white');
        if (whiteOption) {
          send({
            type: 'UPDATE_CUSTOM',
            custom: {
              wand_colour: 'white',
              wand_colour_variant_id: whiteOption.variantId,
              wand_colour_price: whiteOption.price,
            },
          });
        }
      }
    } else if (wand_option === 'No') {
      // Keep wand colour as 'white' but clear variant fields when wand option is No
      send({
        type: 'UPDATE_CUSTOM',
        custom: {
          wand_colour: 'white',
          wand_colour_variant_id: undefined as any,
          wand_colour_price: 0 as any,
        },
      });
    }
  }, [wand_option, wandColourOptions.length, wand_colour_variant_id, wand_colour_price, send]);

  return (
    <SectionContent>
      <Heading>Wand Colour</Heading>
      <Caption>
        Choose your preferred wand colour for operating the blind.
      </Caption>
      <Selector>
        {wandColourOptions.map(option => {
          const { id, title, price, variantId } = option;
          return (
            <SelectorItem
              isSelected={wand_colour_variant_id === variantId}
              onClick={onClick(id, price, variantId, send)}
              key={`${id}-${variantId}`}
            >
              {title} <Extra> + {getFormatedPrice(price)} </Extra>
            </SelectorItem>
          );
        })}
      </Selector>
    </SectionContent>
  );
};
